<template>
    <data-table
        :auth="auth"
        :collection="collection"
        :delete-modal-title="$t('budget.delete_confirm')"
        :is-loading="isLoading"
        :additional-columns="additionalColumns"
        has-comparison-columns
    >
        <template v-slot:column-header="slot">
            {{ $moment(slot.column.entity.date).format('MMMM') }}
        </template>
    </data-table>
</template>

<script>
import BudgetEntry from '@/Models/BudgetEntry';
import Budget from '@/Models/Budget';
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import Store from '@/Models/Store';
import Review from '@/Models/Review';
import moment from 'moment';
import BudgetProjection from '@/Models/BudgetProjection';
import Actual from '@/Models/Actual';
import FieldsModel from '@/Models/Contracts/FieldsModel';

export default {
    components: {DataTable},

    props: {
        review: Review,
        store: Store,
        reviewable: Object,
    },

    metaInfo() {
        return {
            title: this.$t('general.review') +
                (!!this.budget ? ('Budget - ' + this.budget.name + ' ' + this.budget.year) : ''),
        };
    },

    data: function() {
        return {
            collection: null,
            budget: null,
            budgetProjection: null,
            actuals: null,
            isLoading: true,
        };
    },

    created() {
        Budget.with(['entries', 'reviews']).$find(this.reviewable.id).then(async budget => {
            this.budget = budget;
            this.$emit('update:locked', this.budget.is_locked);
            let budgetPreviousYear = budget.year - 1;
            let allMonths = _.range(0, 12)
                .map(num => moment.utc().year(budgetPreviousYear).month(num).startOf('month').format('YYYY-MM-DD'));

            let budgetProjectionRequest = BudgetProjection.with(['entries'])
                .where('store_id', this.store.id).where('year', budgetPreviousYear).$first();

            let actualsRequest = Actual.where('store_id', this.store.id).whereIn('date', allMonths).$get();

            Promise.all([budgetProjectionRequest, actualsRequest]).then(responses => {
                this.budgetProjection = responses[0];
                this.actuals = responses[1];

                this.isLoading = false;
                this.createCollection();
            });
        });
    },

    computed: {
        additionalColumns() {
            if (!this.accumulatedColumn && !this.yearToDateColumn) {
                return null;
            }

            let columns = [];

            if (this.yearToDateColumn) {
                columns.push(this.yearToDateColumn);
            }

            if (this.accumulatedColumn) {
                columns.push(this.accumulatedColumn);
            }

            return new DataColumnCollection(columns, true);
        },

        accumulatedColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(
                this.collection.columns
                    .filter(column => column.entity instanceof BudgetEntry)
                    .map(column => column.entity),
                false,
            );

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.overall').toString();

            return column;
        },

        yearToDateColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(
                this.collection.columns
                    .filter(column => !(column.entity instanceof BudgetEntry))
                    .map(column => column.entity),
                false,
            );

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.ytd').toString() + ' ' + (this.budget.year - 1);

            return column;
        },
    },

    methods: {
        createCollection() {
            let collection = [];
            let monthRange = _.range(1, 13);

            for (const monthIndex of monthRange) {
                let column = new DataColumn();

                if (this.budget.entries && this.budget.entries.length > 0) {
                    column.entity = this.budget.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budget.year, 'M YYYY'), 'month'),
                    );
                }

                if (!column.entity) {
                    column.entity = new BudgetEntry({
                        store: this.store,
                        date: moment.utc(monthIndex + '-' + this.budget.year, 'M-Y')
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                    }).for(this.budget);
                }

                collection.push(column);

                // Add projection column from previous year if available
                let projectionColumn = new DataColumn();

                if (this.actuals && Array.isArray(this.actuals) && this.actuals.length > 0) {
                    projectionColumn.entity = this.actuals.find(entry => moment.utc(entry.date)
                        .isSame(moment.utc(monthIndex + ' ' + (this.budget.year - 1), 'M YYYY'), 'month'));
                }

                if (
                    !projectionColumn.entity
                    && this.budgetProjection
                    && this.budgetProjection.entries
                    && this.budgetProjection.entries.length > 0
                ) {
                    projectionColumn.entity = this.budgetProjection.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + (this.budget.year - 1), 'M YYYY'), 'month'),
                    );
                }

                if (projectionColumn.entity) {
                    projectionColumn.isLocked = true;
                    projectionColumn.header = projectionColumn.entity.header;
                    collection.push(projectionColumn);
                }
            }

            this.collection = new DataColumnCollection(collection,
                (this.budget?.is_locked ? this.budget?.is_locked : this.budget?.is_in_review) ?? false);
        },
    },
};
</script>
